import {
  Alert,
  Anchor,
  Button,
  Checkbox,
  Group,
  Modal,
  NumberInput,
  Paper,
  Select,
  SimpleGrid,
  Stack,
  Table,
  Text,
  TextInput,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { useDebouncedValue } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { IconAlertCircle, IconArrowLeft, IconFileSearch, IconPencil, IconPrinter } from '@tabler/icons-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { api, izvedi, podatki, type Shema } from '../api/odjemalec';
import { NapakaApi, sporocilo } from '../api/napaka';
import { odpriPdf } from '../api/pdf';
import { datum, denar } from '../oblika/oblika';
import { imeOsebe, naslov } from '../osebe/vrste';
import { Kartica, Podatki, StanjeNalaganja } from '../postavitev/Podatki';
import { GlavaZaslona, ZaslonSeznama } from '../seznam/Seznam';
import { Oznaka } from '../strani/PregledRacunov';
import { IzbiraOsebe } from './IzbiraOsebe';
import { IzpisPreizkusnje } from './IzpisPreizkusnje';
import { RazlikaVCeni } from '../izdaja/RazlikaVCeni';
import { FinanciranjeVozila } from './FinanciranjeVozila';
import { StroskiVozila } from './StroskiVozila';
import { RazclenitevDmv } from './RazclenitevDmv';
import { JavnaPonudbaVozila, PovprasevanjaVozila } from './JavnaPonudbaVozila';
import { SlikeVozila } from './SlikeVozila';
import { OknoBranja, RezultatBranja } from './BranjeDokumentov';
import { zdruzi, type Razlika } from './branje';
import { DokumentiVozila } from './DokumentiVozila';
import { KarticaOpisa } from './OpisVozila';
import { shraniDokumente } from './dokumenti';
import { useSeja } from '../seja/useSeja';
import { usePravica } from '../seja/pravice';
import { nazivVozila, stanjeVozila, stevilo } from './vozilo';
import { PomocIkona } from '../pomoc/PomocIkona';

type Vozilo = Shema['VoziloPodatki'];
type Podrobnosti = Shema['VoziloPodrobnosti'];

type Vrsta = 'besedilo' | 'celo' | 'decimalno' | 'denar' | 'datum' | 'da-ne' | 'izbira';
type Polje = {
  k: keyof Vozilo;
  oznaka: string;
  vrsta: Vrsta;
  obvezno?: boolean;
  /** Samo prikaz (vrednost določi strežnik). */
  prikaz?: boolean;
  izbire?: { value: string; label: string }[];
  opis?: string;
};

/**
 * Podrobnosti vozila: sklopi Vozilo, Nakup, Prodaja, Prevzem in status, DMV,
 * izpisi in odstranitev iz zaloge, desno predračuni, računi, dodatni stroški in prodajalec. »Uredi« omogoči urejanje,
 * »Shrani« shrani (nabavno ceno, DMV in posredniško pogodbo uskladi strežnik); pot …/novo je nov vnos (DodajVoziloFrame).
 */
export function PodrobnostiVozila() {
  const { id } = useParams();
  const novo = id === 'novo';
  const poizvedba = useQuery({
    queryKey: ['vozilo', Number(id)],
    enabled: !novo,
    queryFn: ({ signal }) =>
      podatki(api.GET('/api/vozila/{id}/podrobnosti', { signal, params: { path: { id: Number(id) } } })),
  });
  if (!novo && !poizvedba.data) {
    return (
      <StanjeNalaganja
        napaka={poizvedba.error}
        ponovi={() => void poizvedba.refetch()}
        naslov="Vozila ni bilo mogoče naložiti"
      />
    );
  }
  return (
    <ObrazecVozila key={`${id}-${poizvedba.dataUpdatedAt}`} novo={novo} podrobnosti={novo ? NOVO : poizvedba.data!} />
  );
}

const danes = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const NOVO: Podrobnosti = {
  vozilo: { naZalogi: true, cena: 0, kilometri: 0, mocMotorja: 0, prostorninaMotorja: 0, dmv: 0, dmvCikel: 'WLTP' },
  stroski: [],
  racuni: [],
  predracuni: [],
};

function ObrazecVozila({ novo, podrobnosti }: { novo: boolean; podrobnosti: Podrobnosti }) {
  const navigate = useNavigate();
  const odjemalec = useQueryClient();
  const shranjeno: Vozilo & { posredniska?: boolean } = {
    ...podrobnosti.vozilo,
    datumNabave: podrobnosti.vozilo?.datumNabave ?? (novo ? danes() : undefined),
    posredniska: !!podrobnosti.vozilo?.posredniskaId,
  };
  const [urejanje, setUrejanje] = useState(novo);
  const [vneseno, setObrazec] = useState(shranjeno);
  const [prodajalec, setProdajalec] = useState<Shema['OsebaPodatki'] | null>(podrobnosti.vozilo?.prodajalec ?? null);
  const [polja, setPolja] = useState<Record<string, string>>({});
  const [napaka, setNapaka] = useState<string | null>(null);
  const [shranjujem, setShranjujem] = useState(false);
  // branje dokumentov z umetno inteligenco: izpolnjena polja, razlike, datoteke za shranjevanje k vozilu
  const seja = useSeja();
  const aiNaVoljo = seja.status === 'prijavljen' && !!seja.instanca.umetnaInteligenca;
  const [branje, setBranje] = useState(false);
  const [prebrano, setPrebrano] = useState<{ r: Shema['BranjeDokumentov']; razlike: Razlika[] } | null>(null);
  const [izDokumenta, setIzDokumenta] = useState<string[]>([]);
  const [dokumenti, setDokumenti] = useState<File[]>([]);

  const vrste = useQuery({
    queryKey: ['vrste-vozil'],
    queryFn: ({ signal }) => podatki(api.GET('/api/vrste-vozil', { signal })),
  });
  const sifrantiDmv = useQuery({
    queryKey: ['dmv-sifranti'],
    queryFn: ({ signal }) => podatki(api.GET('/api/dmv/sifranti', { signal })),
    staleTime: Infinity,
  });
  const nastavitve = useQuery({
    queryKey: ['nastavitve'],
    queryFn: ({ signal }) => podatki(api.GET('/api/nastavitve', { signal })),
  });
  const zavezanec = nastavitve.data?.zavezanecZaDDV === 'true';
  // nabavne cene in stroški: brez pregleda skriti, brez urejanja samo za ogled (strežnik jih tudi ne pošlje/ohrani)
  const nabavne = usePravica('NABAVNE_CENE');
  const urejanjeNabavnih = usePravica('NABAVNE_CENE', 'UREJANJE');

  // novo vozilo: prva vrsta iz šifranta (kot DodajVoziloFrame)
  const zVrsto = { ...vneseno, vrsta: vneseno.vrsta ?? (novo ? vrste.data?.[0]?.naziv : undefined) };

  // DMV se med urejanjem izračuna na strežniku, ko so vneseni podatki (sicer ostane, kot je)
  const zaDmv: Shema['DmvZahteva'] = {
    vrsta: zVrsto.vrsta,
    mocMotorja: vneseno.mocMotorja ?? 0,
    datumPrveReg: vneseno.datumPrveReg,
    datumNabave: vneseno.datumNabave,
    dmvGorivo: vneseno.dmvGorivo,
    dmvCo2: vneseno.dmvCo2,
    dmvCikel: vneseno.dmvCikel,
    dmvEuro: vneseno.dmvEuro,
    dmvNajvecjaHitrost: vneseno.dmvNajvecjaHitrost,
    dmvOsemSedezev: vneseno.dmvOsemSedezev,
    dmvVecnamensko: vneseno.dmvVecnamensko,
    dmvNovo: vneseno.dmvNovo,
  };
  const [zaDmvZakasnjeno] = useDebouncedValue(zaDmv, 300);
  const dmv = useQuery({
    queryKey: ['dmv-izracun', zaDmvZakasnjeno],
    enabled: urejanje,
    queryFn: ({ signal }) => podatki(api.POST('/api/dmv/izracun', { signal, body: zaDmvZakasnjeno })),
  });
  const obrazec = urejanje && dmv.data?.izracunan ? { ...zVrsto, dmv: dmv.data.skupaj } : zVrsto;

  // brskalnik vpraša pred zaprtjem/osvežitvijo strani z neshranjenimi spremembami
  const spremenjeno =
    urejanje &&
    (JSON.stringify(vneseno) !== JSON.stringify(shranjeno) ||
      (prodajalec?.id ?? null) !== (podrobnosti.vozilo?.prodajalec?.id ?? null));
  useEffect(() => {
    if (!spremenjeno) {
      return;
    }
    const opozori = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener('beforeunload', opozori);
    return () => window.removeEventListener('beforeunload', opozori);
  }, [spremenjeno]);

  const nastavi = (k: keyof Vozilo | 'posredniska', v: unknown) => setObrazec((o) => ({ ...o, [k]: v }));

  const shrani = async () => {
    setNapaka(null);
    setPolja({});
    setShranjujem(true);
    const telo: Vozilo = {
      ...obrazec,
      prodajalec: undefined,
      kupec: undefined,
      // prikaz na spletu ima svoje stikalo (kartica Javna ponudba)
      javno: undefined,
      opis: obrazec.opis ?? '',
      prodajalecId: prodajalec?.id,
      posredniska: !!obrazec.posredniska,
    };
    try {
      const v = novo
        ? await podatki(api.POST('/api/vozila', { body: telo }))
        : await podatki(api.PUT('/api/vozila/{id}', { params: { path: { id: obrazec.id! } }, body: telo }));
      await odjemalec.invalidateQueries({ queryKey: ['vozila'] });
      await odjemalec.invalidateQueries({ queryKey: ['vozilo', v.id] });
      notifications.show({ color: 'green', message: novo ? 'Vozilo je dodano.' : 'Spremembe so shranjene.' });
      if (dokumenti.length > 0) {
        const neshranjeni = await shraniDokumente(v.id!, dokumenti);
        await odjemalec.invalidateQueries({ queryKey: ['dokumenti-vozila', v.id] });
        setDokumenti([]);
        if (neshranjeni.length > 0) {
          notifications.show({
            color: 'yellow',
            message: `Dokumentov ni bilo mogoče shraniti k vozilu: ${neshranjeni.join(', ')}.`,
          });
        }
      }
      setPrebrano(null);
      setIzDokumenta([]);
      if (novo) {
        void navigate(`/vozila/${v.id}`, { replace: true });
      } else {
        setUrejanje(false);
      }
    } catch (e) {
      if (e instanceof NapakaApi && Object.keys(e.polja).length > 0) {
        setPolja(e.polja);
        setNapaka('Popravite označena polja.');
      } else {
        setNapaka(sporocilo(e));
      }
    } finally {
      setShranjujem(false);
    }
  };

  const preklici = () => {
    if (novo) {
      void navigate('/vozila');
      return;
    }
    setObrazec(shranjeno);
    setProdajalec(podrobnosti.vozilo?.prodajalec ?? null);
    setPrebrano(null);
    setIzDokumenta([]);
    setDokumenti([]);
    setPolja({});
    setNapaka(null);
    setUrejanje(false);
  };

  const izbire = (s?: { koda?: string; naziv?: string }[]) =>
    (s ?? []).map((i) => ({ value: i.koda ?? '', label: i.naziv ?? '' }));
  const VOZILO: Polje[] = [
    { k: 'znamka', oznaka: 'Znamka', vrsta: 'besedilo', obvezno: true },
    { k: 'tip', oznaka: 'Tip', vrsta: 'besedilo' },
    { k: 'vin', oznaka: 'VIN', vrsta: 'besedilo' },
    { k: 'tipMotorja', oznaka: 'Tip motorja', vrsta: 'besedilo' },
    { k: 'mocMotorja', oznaka: 'Moč motorja (kW)', vrsta: 'decimalno' },
    { k: 'prostorninaMotorja', oznaka: 'Prostornina (ccm)', vrsta: 'celo' },
    { k: 'barva', oznaka: 'Barva', vrsta: 'besedilo' },
    { k: 'datumPrveReg', oznaka: 'Prva registracija', vrsta: 'datum', obvezno: true },
    { k: 'kilometri', oznaka: 'Kilometri', vrsta: 'celo' },
    { k: 'regSt', oznaka: 'Reg. številka', vrsta: 'besedilo' },
    {
      k: 'vrsta',
      oznaka: 'Vrsta vozila',
      vrsta: 'izbira',
      obvezno: true,
      izbire: (vrste.data ?? []).map((v) => ({ value: v.naziv ?? '', label: v.naziv ?? '' })),
    },
  ];
  const NAKUP: Polje[] = [
    { k: 'datumNabave', oznaka: 'Datum nakupa', vrsta: 'datum', obvezno: true },
    ...(nabavne
      ? [{ k: 'nakupnaCena', oznaka: 'Nakupna cena (bruto)', vrsta: 'denar', prikaz: !urejanjeNabavnih } as Polje]
      : []),
    { k: 'ddvStopnja', oznaka: 'Stopnja DDV (%)', vrsta: 'decimalno' },
    {
      k: 'izkaziDdv',
      oznaka: 'Izkaži DDV',
      vrsta: 'da-ne',
      opis: zavezanec ? undefined : 'Samo za zavezance za DDV (Nastavitve → Podjetje).',
    },
    ...(nabavne
      ? [
          {
            k: 'kupljenaCena',
            // pri izkazanem DDV je nabavna cena brez DDV (strežnik), prodajna pa vedno z DDV
            oznaka: obrazec.izkaziDdv ? 'Nabavna cena brez DDV (nakup + stroški)' : 'Nabavna cena (nakup + stroški)',
            vrsta: 'denar',
            prikaz: true,
          } as Polje,
        ]
      : []),
  ];
  const PRODAJA: Polje[] = [
    { k: 'cena', oznaka: 'Prodajna cena (z DDV)', vrsta: 'denar' },
    { k: 'prodanaCena', oznaka: 'Prodana cena (z DDV)', vrsta: 'denar', prikaz: true },
    { k: 'datumProdaje', oznaka: 'Datum prodaje', vrsta: 'datum', prikaz: true },
    { k: 'vezanDokument', oznaka: 'Vezan dokument', vrsta: 'besedilo' },
  ];
  const PREVZEM: Polje[] = [
    { k: 'stPrevzemnegaDokumenta', oznaka: 'Št. prevz. dok.', vrsta: 'besedilo' },
    { k: 'datumPrevzemnegaDokumenta', oznaka: 'Datum prevz. dok.', vrsta: 'datum' },
    { k: 'posredniska', oznaka: 'Posredniška', vrsta: 'da-ne' },
    { k: 'datumVracila', oznaka: 'Vrnitev', vrsta: 'datum', prikaz: true },
    { k: 'datumRazgradnje', oznaka: 'Razgradnja', vrsta: 'datum', prikaz: true },
  ];
  const DMV: Polje[] = [
    { k: 'dmvGorivo', oznaka: 'Gorivo', vrsta: 'izbira', izbire: izbire(sifrantiDmv.data?.goriva) },
    { k: 'dmvCo2', oznaka: 'Izpust CO2 (g/km)', vrsta: 'decimalno' },
    { k: 'dmvCikel', oznaka: 'Meritev CO2', vrsta: 'izbira', izbire: izbire(sifrantiDmv.data?.cikli) },
    { k: 'dmvEuro', oznaka: 'Standard EURO', vrsta: 'izbira', izbire: izbire(sifrantiDmv.data?.euro) },
    { k: 'dmvNajvecjaHitrost', oznaka: 'Največja hitrost (km/h)', vrsta: 'decimalno' },
    { k: 'dmvOsemSedezev', oznaka: 'Več kot 8 sedežev', vrsta: 'da-ne' },
    { k: 'dmvVecnamensko', oznaka: 'Osebno večnamensko vozilo', vrsta: 'da-ne' },
    { k: 'dmvNovo', oznaka: 'Novo vozilo (še ni bilo registrirano)', vrsta: 'da-ne' },
    { k: 'dmv', oznaka: 'DMV', vrsta: 'denar', prikaz: true },
  ];

  // polja, ki jih branje dokumentov sme izpolniti (ključ → oznaka za razlike)
  const oznakeBranja = Object.fromEntries(
    [...VOZILO, ...NAKUP, ...DMV].filter((p) => !p.prikaz).map((p) => [p.k, p.oznaka]),
  );
  const obPrebranem = (r: Shema['BranjeDokumentov'], datoteke: File[]) => {
    const z = zdruzi(vneseno, (r.vozilo ?? {}) as Record<string, unknown>, oznakeBranja);
    setObrazec(z.obrazec);
    setIzDokumenta((i) => [...new Set([...i, ...z.izpolnjeno])]);
    setPrebrano({ r, razlike: z.razlike });
    setDokumenti((d) => [...d, ...datoteke]);
    if (r.obstojeciProdajalec && !prodajalec) {
      setProdajalec(r.obstojeciProdajalec);
    }
  };
  const uporabi = (razlike: Razlika[]) => {
    setObrazec((o) => ({ ...o, ...Object.fromEntries(razlike.map((r) => [r.kljuc, r.prebrano])) }));
    setIzDokumenta((i) => [...new Set([...i, ...razlike.map((r) => r.kljuc)])]);
    setPrebrano((p) => p && { ...p, razlike: p.razlike.filter((r) => !razlike.includes(r)) });
  };

  const vnos = (p: Polje): ReactNode => {
    const vrednost = (obrazec as Record<string, unknown>)[p.k];
    const skupno = {
      label: p.oznaka,
      error: polja[p.k],
      withAsterisk: p.obvezno,
      size: 'sm' as const,
      description: urejanje && izDokumenta.includes(p.k) ? 'Iz dokumenta' : undefined,
    };
    if (p.prikaz) {
      return <TextInput key={p.k} {...skupno} value={prikazi(p, vrednost)} readOnly variant="filled" />;
    }
    switch (p.vrsta) {
      case 'da-ne':
        return (
          <Checkbox
            key={p.k}
            label={p.oznaka}
            description={p.opis ?? skupno.description}
            checked={!!vrednost}
            disabled={p.k === 'izkaziDdv' && !zavezanec}
            onChange={(e) => nastavi(p.k, e.currentTarget.checked)}
            error={polja[p.k]}
            mt={6}
          />
        );
      case 'datum':
        return (
          <DatePickerInput
            key={p.k}
            {...skupno}
            value={(vrednost as string | undefined) ?? null}
            onChange={(v) => nastavi(p.k, v ?? undefined)}
            valueFormat="D. M. YYYY"
            clearable={!p.obvezno}
          />
        );
      case 'izbira':
        return (
          <Select
            key={p.k}
            {...skupno}
            // šifrant ima lahko podvojene nazive (npr. dve vrsti »VILIČAR«), Select jih ne dovoli
            data={[...new Map((p.izbire ?? []).map((i) => [i.value, i])).values()]}
            value={(vrednost as string | undefined) ?? null}
            onChange={(v) => nastavi(p.k, v ?? undefined)}
            placeholder="Ni vneseno"
            clearable={!p.obvezno}
            allowDeselect={!p.obvezno}
            searchable
          />
        );
      case 'besedilo':
        return (
          <TextInput
            key={p.k}
            {...skupno}
            value={String(vrednost ?? '')}
            onChange={(e) => nastavi(p.k, p.k === 'vin' ? e.currentTarget.value.toUpperCase() : e.currentTarget.value)}
          />
        );
      default:
        return (
          <NumberInput
            key={p.k}
            {...skupno}
            value={(vrednost as number | undefined) ?? ''}
            onChange={(v) =>
              nastavi(p.k, p.vrsta === 'celo' || p.vrsta === 'decimalno' ? (stevilo(v) ?? undefined) : stevilo(v))
            }
            decimalScale={p.vrsta === 'celo' ? 0 : 2}
            allowDecimal={p.vrsta !== 'celo'}
            decimalSeparator=","
            thousandSeparator={p.vrsta === 'denar' || p.vrsta === 'celo' ? '.' : undefined}
            rightSection={p.vrsta === 'denar' ? '€' : undefined}
          />
        );
    }
  };
  const sklop = (seznam: Polje[]) =>
    urejanje ? (
      <SimpleGrid cols={{ base: 1, xs: 2 }} spacing="sm">
        {seznam.map(vnos)}
      </SimpleGrid>
    ) : (
      <Podatki vrstice={seznam.map((p) => [p.oznaka, prikazi(p, (obrazec as Record<string, unknown>)[p.k])])} />
    );

  const v = podrobnosti.vozilo ?? {};
  const stanje = stanjeVozila(v);

  return (
    <ZaslonSeznama>
      <Button component={Link} to="/vozila" variant="subtle" leftSection={<IconArrowLeft size={16} />} mb="xs" px={0}>
        Vozila
      </Button>
      <GlavaZaslona
        naslov={novo ? 'Novo vozilo' : nazivVozila(v)}
        opis={novo ? 'Vnesite podatke vozila in prodajalca.' : [v.vin, v.regSt].filter(Boolean).join(' · ')}
        dejanja={
          urejanje ? (
            <Group gap="xs">
              {aiNaVoljo && (
                <Group gap={4} wrap="nowrap">
                  <Button variant="light" leftSection={<IconFileSearch size={18} />} onClick={() => setBranje(true)}>
                    Preberi iz dokumentov
                  </Button>
                  <PomocIkona clanek="branje-dokumentov" />
                </Group>
              )}
              <Button variant="subtle" onClick={preklici} disabled={shranjujem}>
                Prekliči
              </Button>
              <Button onClick={() => void shrani()} loading={shranjujem}>
                {novo ? 'Dodaj' : 'Shrani'}
              </Button>
            </Group>
          ) : (
            <Button leftSection={<IconPencil size={18} />} onClick={() => setUrejanje(true)}>
              Uredi
            </Button>
          )
        }
      />
      {!novo && (stanje || v.posredniskaId) && (
        <Group gap={6} mb="sm" mt={-8}>
          {stanje && <Oznaka barva={stanje.barva}>{stanje.naziv}</Oznaka>}
          {podrobnosti.posredniska && (
            <Oznaka barva="grape">posredniška {podrobnosti.posredniska.stevilka ?? ''}</Oznaka>
          )}
        </Group>
      )}
      {napaka && (
        <Alert color="red" variant="light" icon={<IconAlertCircle size={18} />} mb="md" role="alert">
          {napaka}
        </Alert>
      )}
      {urejanje && prebrano && (
        <Stack mb="md">
          <RezultatBranja
            rezultat={prebrano.r}
            izpolnjeno={izDokumenta.length}
            razlike={prebrano.razlike}
            dokumentov={dokumenti.length}
            uporabi={uporabi}
            prodajalec={prodajalec}
            izberiProdajalca={setProdajalec}
            zapri={() => setPrebrano(null)}
          />
        </Stack>
      )}
      <OknoBranja odprto={branje} zapri={() => setBranje(false)} prebrano={obPrebranem} />
      <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="md">
        <Stack gap="md">
          <Kartica naslov="Vozilo">{sklop(VOZILO)}</Kartica>
          <KarticaOpisa
            urejanje={urejanje}
            obrazec={obrazec}
            voziloId={novo ? undefined : v.id}
            slik={podrobnosti.slike?.length ?? 0}
            aiNaVoljo={aiNaVoljo}
            nastavi={(o) => nastavi('opis', o)}
            napaka={polja.opis}
          />
          <Kartica naslov="Nakup">{sklop(NAKUP)}</Kartica>
          <Kartica naslov="Prodaja">{sklop(PRODAJA)}</Kartica>
          <Kartica naslov="Prevzem in status">{sklop(PREVZEM)}</Kartica>
          <Kartica naslov="DMV" dejanja={<PomocIkona clanek="izracun-dmv" />}>
            {urejanje && (
              <Text size="sm" c="dimmed" mb="sm">
                Ko so vneseni podatki za DMV, se DMV izračuna sam (ZDMV-1); vrsta, moč in datumi so iz podatkov vozila.
              </Text>
            )}
            {sklop(DMV)}
            {urejanje && <RazclenitevDmv izracun={dmv.data} />}
          </Kartica>
          {!novo && <Izpisi podrobnosti={podrobnosti} />}
          {!novo && <Odstranitev podrobnosti={podrobnosti} />}
        </Stack>
        <Stack gap="md">
          {!novo && <JavnaPonudbaVozila podrobnosti={podrobnosti} urejanje={urejanje} />}
          {!novo && <SlikeVozila voziloId={v.id!} zacetne={podrobnosti.slike ?? []} />}
          {!novo && <DokumentiVozila voziloId={v.id!} />}
          {!novo && <PovprasevanjaVozila povprasevanja={podrobnosti.povprasevanja ?? []} />}
          <Kartica
            naslov="Prodajalec"
            dejanja={
              !urejanje && prodajalec?.id ? (
                <Button size="xs" variant="light" component={Link} to={`/osebe/prodajalci/${prodajalec.id}`}>
                  Uredi prodajalca
                </Button>
              ) : undefined
            }
          >
            {urejanje && (
              <IzbiraOsebe
                vrsta="prodajalci"
                oznaka="Prodajalec"
                izbran={prodajalec}
                izberi={setProdajalec}
                napaka={polja.prodajalecId}
              />
            )}
            {prodajalec ? (
              <Stack mt={urejanje ? 'sm' : 0}>
                <Podatki
                  vrstice={[
                    ['Ime', imeOsebe(prodajalec)],
                    ['Naslov', naslov(prodajalec)],
                    ['Davčna št.', prodajalec.davcna],
                    ['EMŠO', prodajalec.emso],
                    ['Mat. št.', prodajalec.maticnaSt],
                    ['OIB', prodajalec.oib],
                    ['Št. dok.', prodajalec.stDokumenta],
                  ]}
                />
              </Stack>
            ) : (
              !urejanje && (
                <Text size="sm" c="dimmed">
                  Ni prodajalca.
                </Text>
              )
            )}
          </Kartica>
          {novo ? (
            <Text size="sm" c="dimmed">
              Dodatne stroške, izpise in odstranitev iz zaloge urejate, ko je vozilo dodano.
            </Text>
          ) : (
            <>
              {nabavne && (
                <StroskiVozila
                  voziloId={v.id!}
                  stroski={podrobnosti.stroski ?? []}
                  skupaj={podrobnosti.stroskiSkupaj ?? 0}
                />
              )}
              <FinanciranjeVozila
                voziloId={v.id!}
                financiranja={podrobnosti.financiranja ?? []}
                lahkoFinancira={!!podrobnosti.lahkoFinancira}
              />
              {podrobnosti.rvc && <RazlikaVCeni rvc={[podrobnosti.rvc]} naslov="Razlika v ceni ob prodaji" />}
              <Kartica naslov="Računi">
                <Dokumenti
                  vrstice={(podrobnosti.racuni ?? []).map((r) => ({
                    id: r.id!,
                    na: `/racuni/${r.id}`,
                    stevilka: r.stRacuna,
                    datum: r.datum,
                    znesek: r.znesek,
                    kupec: r.kupec,
                    oznaka: r.storniran ? 'storniran' : undefined,
                  }))}
                  prazno="Vozilo ni na nobenem računu."
                />
              </Kartica>
              <Kartica naslov="Predračuni">
                <Dokumenti
                  vrstice={(podrobnosti.predracuni ?? []).map((p) => ({
                    id: p.id!,
                    na: `/predracuni/${p.id}`,
                    stevilka: p.stPredracuna,
                    datum: p.datum,
                    znesek: p.znesek,
                    kupec: p.kupec,
                  }))}
                  prazno="Vozilo ni na nobenem predračunu."
                />
              </Kartica>
            </>
          )}
        </Stack>
      </SimpleGrid>
      {urejanje && (
        // na telefonu je obrazec dolg: gumba ostaneta vidna na dnu zaslona
        <Paper
          hiddenFrom="sm"
          shadow="md"
          p="sm"
          mt="md"
          radius="lg"
          style={{ position: 'sticky', bottom: 8, zIndex: 10 }}
        >
          <Group grow gap="xs">
            <Button variant="default" onClick={preklici} disabled={shranjujem}>
              Prekliči
            </Button>
            <Button onClick={() => void shrani()} loading={shranjujem}>
              {novo ? 'Dodaj' : 'Shrani'}
            </Button>
          </Group>
        </Paper>
      )}
    </ZaslonSeznama>
  );
}

function prikazi(p: Polje, vrednost: unknown): string {
  if (vrednost === undefined || vrednost === null || vrednost === '') {
    return '';
  }
  switch (p.vrsta) {
    case 'da-ne':
      return vrednost ? 'Da' : 'Ne';
    case 'datum':
      return datum(String(vrednost));
    case 'denar':
      return `${denar(Number(vrednost))} €`;
    case 'izbira':
      return p.izbire?.find((i) => i.value === vrednost)?.label ?? String(vrednost);
    case 'decimalno':
      return String(vrednost).replace('.', ',');
    default:
      return String(vrednost);
  }
}

/** Računi ali predračuni z vozilom; klik odpre dokument. */
function Dokumenti({
  vrstice,
  prazno,
}: {
  vrstice: {
    id: number;
    na: string;
    stevilka?: string;
    datum?: string;
    znesek?: number;
    kupec?: string;
    oznaka?: string;
  }[];
  prazno: string;
}) {
  if (vrstice.length === 0) {
    return (
      <Text size="sm" c="dimmed">
        {prazno}
      </Text>
    );
  }
  return (
    <Table.ScrollContainer minWidth={420}>
      <Table className="stevilke" verticalSpacing={6}>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Številka</Table.Th>
            <Table.Th>Datum</Table.Th>
            <Table.Th>Kupec</Table.Th>
            <Table.Th ta="right">Znesek</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {vrstice.map((d) => (
            <Table.Tr key={d.id}>
              <Table.Td className="brez-preloma">
                <Anchor component={Link} to={d.na} fw={600}>
                  {d.stevilka}
                </Anchor>
                {d.oznaka && (
                  <>
                    {' '}
                    <Oznaka barva="red">{d.oznaka}</Oznaka>
                  </>
                )}
              </Table.Td>
              <Table.Td className="brez-preloma">{datum(d.datum)}</Table.Td>
              <Table.Td>{d.kupec}</Table.Td>
              <Table.Td ta="right" className="brez-preloma">
                {denar(d.znesek)}
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </Table.ScrollContainer>
  );
}

/** Izpisi vozila (PDF s strežnika iz predlog); posredniška samo za vozilo s pogodbo; preizkušnja z obrazcem. */
function Izpisi({ podrobnosti }: { podrobnosti: Podrobnosti }) {
  const id = podrobnosti.vozilo!.id!;
  const [dela, setDela] = useState<string | null>(null);
  const [napaka, setNapaka] = useState<string | null>(null);
  const [preizkusnja, setPreizkusnja] = useState(false);
  // posredniška in kupoprodajna pogodba vsebujeta nakupno ceno
  const nabavne = usePravica('NABAVNE_CENE');
  const izpisi = [
    ...(podrobnosti.vozilo?.posredniskaId && nabavne
      ? [{ naziv: 'Posredniška', klic: () => api.GET('/api/vozila/{id}/posredniska/pdf', zahteva(id)) }]
      : []),
    { naziv: 'Prevzemni zapisnik', klic: () => api.GET('/api/vozila/{id}/prevzemni/pdf', zahteva(id)) },
    ...(nabavne
      ? [{ naziv: 'Kupoprodajna', klic: () => api.GET('/api/vozila/{id}/kupoprodajna/pdf', zahteva(id)) }]
      : []),
    { naziv: 'Prevzemnica', klic: () => api.GET('/api/vozila/{id}/prevzemnica/pdf', zahteva(id)) },
  ];
  const natisni = async (naziv: string, klic: () => ReturnType<typeof api.GET>) => {
    setNapaka(null);
    setDela(naziv);
    try {
      await odpriPdf(klic());
    } catch (e) {
      setNapaka(sporocilo(e));
    } finally {
      setDela(null);
    }
  };
  return (
    <Kartica naslov="Izpisi">
      <SimpleGrid cols={2} spacing="xs">
        {izpisi.map((i) => (
          <Button
            key={i.naziv}
            variant="default"
            leftSection={<IconPrinter size={16} />}
            onClick={() => void natisni(i.naziv, i.klic)}
            loading={dela === i.naziv}
            styles={{ label: { whiteSpace: 'normal', textAlign: 'center' } }}
          >
            {i.naziv}
          </Button>
        ))}
        <Button
          variant="default"
          leftSection={<IconPrinter size={16} />}
          onClick={() => setPreizkusnja(true)}
          styles={{ label: { whiteSpace: 'normal', textAlign: 'center' } }}
        >
          Preizkušnja
        </Button>
      </SimpleGrid>
      <IzpisPreizkusnje odprto={preizkusnja} zapri={() => setPreizkusnja(false)} vozilo={podrobnosti.vozilo} />
      {!podrobnosti.vozilo?.posredniskaId && (
        <Text size="xs" c="dimmed" mt="xs">
          Posredniška pogodba: označite »Posredniška« pri urejanju vozila.
        </Text>
      )}
      {napaka && (
        <Alert color="red" variant="light" mt="sm" role="alert">
          {napaka}
        </Alert>
      )}
    </Kartica>
  );
}

const zahteva = (id: number) => ({ params: { path: { id } }, parseAs: 'blob' as const });

/** Vračilo ali razgradnja (odstranitev iz zaloge) z datumom; samo za vozilo na zalogi. */
function Odstranitev({ podrobnosti }: { podrobnosti: Podrobnosti }) {
  const odjemalec = useQueryClient();
  const id = podrobnosti.vozilo!.id!;
  const [vrsta, setVrsta] = useState<'vracilo' | 'razgradnja' | null>(null);
  const [dan, setDan] = useState<string | null>(danes());
  const [napaka, setNapaka] = useState<string | null>(null);
  const [dela, setDela] = useState(false);

  const potrdi = async () => {
    if (!vrsta || !dan) {
      setNapaka('Izberite datum.');
      return;
    }
    setNapaka(null);
    setDela(true);
    try {
      const telo = { params: { path: { id } }, body: { datum: dan } };
      await izvedi(
        vrsta === 'vracilo'
          ? api.POST('/api/vozila/{id}/vracilo', telo)
          : api.POST('/api/vozila/{id}/razgradnja', telo),
      );
      await odjemalec.invalidateQueries({ queryKey: ['vozilo', id] });
      await odjemalec.invalidateQueries({ queryKey: ['vozila'] });
      notifications.show({ color: 'green', message: 'Vozilo je odstranjeno iz zaloge.' });
      setVrsta(null);
    } catch (e) {
      setNapaka(sporocilo(e));
    } finally {
      setDela(false);
    }
  };

  return (
    <Kartica naslov="Odstranitev iz zaloge">
      {podrobnosti.lahkoOdstrani ? (
        <SimpleGrid cols={2} spacing="xs">
          <Button variant="default" onClick={() => setVrsta('vracilo')}>
            Vračilo
          </Button>
          <Button variant="default" onClick={() => setVrsta('razgradnja')}>
            Razgradnja
          </Button>
        </SimpleGrid>
      ) : (
        <Text size="sm" c="dimmed">
          Vozilo ni na zalogi (prodano, vrnjeno ali razgrajeno), zato ga ni mogoče odstraniti.
        </Text>
      )}
      <Modal
        opened={vrsta !== null}
        onClose={() => !dela && setVrsta(null)}
        title={vrsta === 'vracilo' ? 'Vračilo vozila' : 'Razgradnja vozila'}
        radius="lg"
      >
        <Stack gap="sm">
          <Text size="sm">Vozilo bo odstranjeno iz zaloge. Tega dejanja ni mogoče razveljaviti.</Text>
          <DatePickerInput
            label={vrsta === 'vracilo' ? 'Datum vračila' : 'Datum razgradnje'}
            value={dan}
            onChange={setDan}
            valueFormat="D. M. YYYY"
            error={napaka}
          />
          <Group justify="flex-end" gap="xs">
            <Button variant="subtle" onClick={() => setVrsta(null)} disabled={dela}>
              Prekliči
            </Button>
            <Button color="red" onClick={() => void potrdi()} loading={dela}>
              Odstrani iz zaloge
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Kartica>
  );
}
